import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Tag, Clock, Plus, Check, ShoppingBag, Wind, Snowflake, Utensils,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SpecialMenuDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { addItem, setIsOpen } = useCart();

  const [menu, setMenu] = useState(null);
  const [restaurantItems, setRestaurantItems] = useState([]);
  const [extras, setExtras] = useState({}); // { item_id: quantity }
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/special-menus/${id}`);
        if (res.ok) {
          const data = await res.json();
          setMenu(data);
          // If linked to a restaurant, fetch its menu items so the customer
          // can add extras (drinks, sides, desserts, ...) to the order.
          if (data.restaurant_id) {
            const itemsRes = await fetch(`${API_URL}/api/restaurants/${data.restaurant_id}/menu`);
            if (itemsRes.ok) setRestaurantItems(await itemsRes.json());
          }
        }
      } catch (e) {
        console.error('Special menu fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const adjustExtra = (item_id, delta) => {
    setExtras((prev) => {
      const next = { ...prev };
      const cur = next[item_id] || 0;
      const v = Math.max(0, cur + delta);
      if (v === 0) delete next[item_id];
      else next[item_id] = v;
      return next;
    });
  };

  const extrasTotal = Object.entries(extras).reduce((sum, [iid, qty]) => {
    const it = restaurantItems.find((x) => x.item_id === iid);
    return sum + (it ? it.price * qty : 0);
  }, 0);
  const grandTotal = (menu?.price || 0) + extrasTotal;

  const handleAddToCart = async () => {
    if (!menu) return;
    setAdding(true);
    const restaurantId = menu.restaurant_id || `special_${menu.menu_id}`;
    const restaurantName = menu.name_pt || menu.name;

    // 1) Add the special menu itself as a single line
    addItem({
      item_id: `menu_${menu.menu_id}`,
      name: `🍽️ ${language === 'pt' ? (menu.name_pt || menu.name) : menu.name}`,
      price: Number(menu.price || 0),
      restaurant_id: restaurantId,
      restaurant_name: restaurantName,
    });

    // 2) Add each extra
    Object.entries(extras).forEach(([iid, qty]) => {
      const it = restaurantItems.find((x) => x.item_id === iid);
      if (!it) return;
      for (let i = 0; i < qty; i++) {
        addItem({
          item_id: it.item_id,
          name: it.name,
          price: it.price,
          restaurant_id: restaurantId,
          restaurant_name: restaurantName,
        });
      }
    });

    toast.success(language === 'pt' ? 'Adicionado ao carrinho' : 'Added to cart');
    if (menu.restaurant_id) {
      // Restaurant-linked menu → go straight to checkout
      navigate('/checkout');
    } else {
      // Generic menu (shisha/ice/etc) → open cart drawer to confirm
      setIsOpen(true);
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <Skeleton className="h-10 w-40 mb-4" />
          <Skeleton className="h-72 rounded-3xl mb-6" />
          <Skeleton className="h-48 rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#9FA2B4] mb-4">{language === 'pt' ? 'Menu não encontrado' : 'Menu not found'}</p>
          <Link to="/"><Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">{language === 'pt' ? 'Início' : 'Home'}</Button></Link>
        </div>
      </div>
    );
  }

  const sourceBadge = (() => {
    if (menu.source_type === 'shisha') return <Badge className="bg-purple-500 text-white"><Wind size={12} className="mr-1" /> Shisha</Badge>;
    if (menu.source_type === 'ice') return <Badge className="bg-blue-500 text-white"><Snowflake size={12} className="mr-1" /> {language === 'pt' ? 'Gelo' : 'Ice'}</Badge>;
    return <Badge className="bg-[#2EC4B6] text-white"><Utensils size={12} className="mr-1" /> {language === 'pt' ? 'Restaurante' : 'Restaurant'}</Badge>;
  })();

  const includedItems = Array.isArray(menu.included_items) ? menu.included_items : [];

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="special-menu-detail">
      <div className="container mx-auto px-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-[#004E64]" data-testid="special-menu-back">
          <ArrowLeft size={18} className="mr-1" /> {language === 'pt' ? 'Voltar' : 'Back'}
        </Button>

        {/* Hero */}
        <Card className="rounded-3xl border-[#004E64]/5 overflow-hidden mb-6">
          {menu.image && (
            <div className="relative h-56 md:h-72 overflow-hidden">
              <img src={menu.image.startsWith('http') ? menu.image : `${API_URL}${menu.image}`} alt={menu.name} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 flex gap-2">{sourceBadge}</div>
              {menu.original_price && menu.original_price > menu.price && (
                <Badge className="absolute top-4 right-4 bg-red-500 text-white text-sm">
                  -{Math.round((1 - menu.price / menu.original_price) * 100)}%
                </Badge>
              )}
            </div>
          )}
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Tag size={16} className="text-[#FF6B35]" />
              <h1 className="text-2xl md:text-3xl font-bold text-[#004E64]" data-testid="special-menu-title">
                {language === 'pt' ? (menu.name_pt || menu.name) : menu.name}
              </h1>
            </div>
            <p className="text-[#9FA2B4] mb-4">
              {language === 'pt' ? (menu.description_pt || menu.description) : menu.description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="text-3xl font-bold text-[#FF6B35]">€{Number(menu.price || 0).toFixed(2)}</span>
              {menu.original_price && menu.original_price > menu.price && (
                <span className="text-base text-[#9FA2B4] line-through">€{Number(menu.original_price).toFixed(2)}</span>
              )}
              {menu.available_hours_start && menu.available_hours_end && (
                <Badge variant="outline" className="text-[#2EC4B6] border-[#2EC4B6]">
                  <Clock size={12} className="mr-1" /> {menu.available_hours_start} – {menu.available_hours_end}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Included items */}
        {includedItems.length > 0 && (
          <Card className="rounded-3xl border-[#004E64]/5 mb-6">
            <CardContent className="p-6">
              <h2 className="font-bold text-[#004E64] mb-3 flex items-center gap-2">
                <Check size={16} className="text-[#2EC4B6]" />
                {language === 'pt' ? 'O que está incluído' : 'What is included'}
              </h2>
              <ul className="space-y-2" data-testid="included-items-list">
                {includedItems.map((it, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#2EC4B6]/10 text-[#2EC4B6] flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {it.quantity || 1}×
                    </span>
                    <div>
                      <p className="font-semibold text-[#004E64]">{it.name}</p>
                      {it.description && <p className="text-xs text-[#9FA2B4]">{it.description}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Extras (other items from same restaurant) */}
        {restaurantItems.length > 0 && (
          <Card className="rounded-3xl border-[#004E64]/5 mb-6">
            <CardContent className="p-6">
              <h2 className="font-bold text-[#004E64] mb-1">
                {language === 'pt' ? 'Quer acrescentar algo?' : 'Want to add something?'}
              </h2>
              <p className="text-xs text-[#9FA2B4] mb-4">
                {language === 'pt' ? 'Adicione bebidas, sobremesas ou outros pratos do mesmo restaurante.' : 'Add drinks, desserts or any other items from the same restaurant.'}
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="extras-list">
                {restaurantItems.filter((i) => i.is_available !== false).map((it) => {
                  const qty = extras[it.item_id] || 0;
                  return (
                    <div key={it.item_id} className="flex items-center gap-3 p-3 rounded-xl border border-[#F4F1EA] hover:bg-[#FAFAF7]" data-testid={`extra-row-${it.item_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#004E64] truncate">{it.name}</p>
                        <p className="text-xs text-[#9FA2B4]">{it.category}</p>
                      </div>
                      <span className="text-sm font-bold text-[#FF6B35] w-16 text-right">€{it.price.toFixed(2)}</span>
                      {qty === 0 ? (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => adjustExtra(it.item_id, +1)} data-testid={`extra-add-${it.item_id}`}>
                          <Plus size={14} />
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button size="icon" variant="outline" className="rounded-full h-7 w-7" onClick={() => adjustExtra(it.item_id, -1)} data-testid={`extra-minus-${it.item_id}`}>−</Button>
                          <span className="font-bold text-[#004E64] w-5 text-center" data-testid={`extra-qty-${it.item_id}`}>{qty}</span>
                          <Button size="icon" variant="outline" className="rounded-full h-7 w-7" onClick={() => adjustExtra(it.item_id, +1)} data-testid={`extra-plus-${it.item_id}`}>+</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Card className="rounded-3xl border-[#004E64]/5 sticky bottom-4 shadow-lg">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Total' : 'Total'}</p>
              <p className="text-2xl font-bold text-[#FF6B35]" data-testid="special-menu-total">€{grandTotal.toFixed(2)}</p>
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={adding}
              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full px-6 py-6 text-base font-bold"
              data-testid="special-menu-add-to-cart"
            >
              <ShoppingBag size={18} className="mr-2" />
              {language === 'pt' ? 'Adicionar ao carrinho' : 'Add to cart'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SpecialMenuDetailPage;
